import type { PropertyResult } from '../../../hooks/climateData'
import type { HazardInfo } from '../../../types/climate'
import { MetricState, type CanonicalPeriod, type EmissionTier } from '../common'
import { sumMetricNode, sumNumbers } from '../common/financialMetrics'
import {
  sumAggregateValues,
  sumAvailableComponentValues,
  type AggregateSummary,
  type ComponentAggregateSummary,
} from '../common/portfolioAggregates'
import { parseFinancialValue, type PortfolioRiskProperty } from './portfolioRisk'

export const HAZARD_AAL_CONFIG: Record<
  string,
  { damageMetric: string; biMetric: string; periodized: boolean; damageTargets?: string[] }
> = {
  fld: {
    damageMetric: 'fld:average_damage_loss',
    biMetric: 'fld:average_bi_loss',
    periodized: true,
    damageTargets: ['building', 'contents'],
  },
  wf: {
    damageMetric: 'wf:damage_loss',
    biMetric: 'wf:bi_loss',
    periodized: true,
    damageTargets: ['structure', 'contents'],
  },
  tc: { damageMetric: 'tc:average_damage_loss', biMetric: 'tc:average_bi_loss', periodized: false },
  wind_gust: { damageMetric: 'wind_gust:average_damage_loss', biMetric: 'wind_gust:average_bi_loss', periodized: true },
}

export type HazardAalSummary = {
  damage: AggregateSummary
  bi: AggregateSummary
  combined: ComponentAggregateSummary
}

export type PortfolioHazardAal = {
  hazard: HazardInfo
  modelled: boolean
  periodAvailable: boolean
  loading: boolean
  error: boolean
  summary: HazardAalSummary
  byProperty: Record<number, PropertyAal>
  retry?: () => void
}

export type PropertyAal = { damage: number | null; bi: number | null; combined: number | null }

export function combinePropertyAal(damage: number | null, bi: number | null): PropertyAal {
  return { damage, bi, combined: sumNumbers([damage, bi]) }
}

export function combinedPropertyAal(hazards: PortfolioHazardAal[], id: number): number | null {
  const included = hazards.filter(hazard => hazard.modelled && hazard.periodAvailable)
  if (included.some(hazard => hazard.loading || hazard.error)) return null
  return sumNumbers(included.map(hazard => hazard.byProperty[id]?.combined))
}

export function extractPropertyAal(
  hazardCode: string,
  component: 'damage' | 'bi',
  entry: PropertyResult | undefined,
  emissionTier: EmissionTier,
  period: CanonicalPeriod,
): number | null {
  const config = HAZARD_AAL_CONFIG[hazardCode]
  if (!config || entry?.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  const tierNode = entry.data.data?.[emissionTier]
  const node = config.periodized ? tierNode?.[period] : tierNode
  if (component === 'damage' && config.damageTargets) {
    return sumNumbers(config.damageTargets.map(target => sumMetricNode(node?.[target])))
  }
  return sumMetricNode(node)
}

export function summarizeHazardAal(
  propertyIds: number[],
  damage: Record<number, number | null>,
  bi: Record<number, number | null>,
): HazardAalSummary {
  return {
    damage: sumAggregateValues(propertyIds, id => damage[id]),
    bi: sumAggregateValues(propertyIds, id => bi[id]),
    combined: sumAvailableComponentValues(propertyIds, { damage: id => damage[id], bi: id => bi[id] }),
  }
}

export function summarizeCombinedAal(hazards: PortfolioHazardAal[], propertyIds: number[]) {
  const included = hazards.filter(hazard => hazard.modelled && hazard.periodAvailable)
  const loading = included.some(hazard => hazard.loading)
  const error = included.some(hazard => hazard.error)
  const available = included.filter(hazard => !hazard.loading && !hazard.error)
  const contributingIds = new Set(available.flatMap(hazard => hazard.summary.combined.contributingPropertyIds))
  const propertyIdSet = new Set(propertyIds)
  return {
    value: loading || error ? null : sumNumbers(available.map(hazard => hazard.summary.combined.value)),
    contributors: Array.from(contributingIds).filter(id => propertyIdSet.has(id)).length,
    total: propertyIds.length,
    loading,
    error,
  }
}

export function summarizePortfolioRevenue(properties: PortfolioRiskProperty[]) {
  const byProperty = new Map(
    properties.map(property => [property.id, parseFinancialValue(property.insurance?.revenue)]),
  )
  return sumAggregateValues(
    properties.map(property => property.id),
    id => byProperty.get(id),
  )
}
